import {
  DimeSidebar,
  DimeSidebarContent,
  DimeSidebarFooter,
  DimeSidebarGroup,
  DimeSidebarGroupContent,
  DimeSidebarGroupLabel,
  DimeSidebarHeader,
  DimeSidebarMenu,
  DimeSidebarMenuButton,
  DimeSidebarMenuItem,
  DimeSidebarRail,
  DimeSidebarSeparator,
  DimeSpinner,
  useDimeSidebar,
} from '@nudool/design-system';
import { Link, useRouterState } from '@tanstack/react-router';
import { LogOut } from 'lucide-react';
import { useEffect } from 'react';

import { usePermissions } from '../modules/iam/permissions-context.js';
import { APP_NAV, type AppNavItem } from './app-nav-config.js';
import { isNavItemActive } from './is-nav-item-active.js';
import { NudoolLogo } from './NudoolLogo.js';

type AppSidebarProps = Readonly<{
  onLogout: () => void;
}>;

/**
 * Longest match wins, the same tiebreak `buildAppBreadcrumbs` applies.
 *
 * `/manufacturing` is a prefix of `/manufacturing/work-orders`, so a plain prefix test lights up
 * both rows at once and puts `aria-current="page"` on two links in one nav.
 */
function isItemActive(pathname: string, item: AppNavItem): boolean {
  if (!('to' in item) || !item.to) return false;
  if (!isNavItemActive(pathname, item.to)) return false;

  const itemTo = item.to;
  return !APP_NAV.some((section) =>
    section.items.some(
      (other) =>
        'to' in other &&
        other.to &&
        other.to.length > itemTo.length &&
        isNavItemActive(pathname, other.to),
    ),
  );
}

export function AppSidebar({ onLogout }: AppSidebarProps) {
  const { permissions, hasActiveBrandMembership, loading } = usePermissions();
  const { state, isMobile, setOpenMobile } = useDimeSidebar();
  const pathname = useRouterState({ select: (routerState) => routerState.location.pathname });
  const collapsed = state === 'collapsed' && !isMobile;

  // Clear leftover mobile sheet state when leaving the mobile breakpoint.
  useEffect(() => {
    if (!isMobile) setOpenMobile(false);
  }, [isMobile, setOpenMobile]);

  // Mobile sheet is an overlay — dismiss it after navigation so the destination page is visible.
  useEffect(() => {
    setOpenMobile(false);
  }, [pathname, setOpenMobile]);

  const visibleItems = (items: AppNavItem[]) =>
    items.filter(
      (item) =>
        item.required.every((permission) => permissions.includes(permission)) &&
        (!item.requiresBrandMembership || hasActiveBrandMembership),
    );

  return (
    <DimeSidebar collapsible="icon" role="navigation" aria-label="Main navigation">
      <DimeSidebarHeader className="h-14 flex-row items-center gap-2 border-b border-sidebar-border p-2">
        <div className={collapsed ? 'flex w-full justify-center' : 'min-w-0 px-1'}>
          <NudoolLogo showWordmark={!collapsed} />
        </div>
      </DimeSidebarHeader>

      <DimeSidebarContent>
        {loading ? (
          <div className="flex flex-1 items-center justify-center py-8">
            <DimeSpinner />
          </div>
        ) : (
          APP_NAV.map((section) => {
            const visible = visibleItems(section.items);
            if (!visible.length) return null;

            return (
              <DimeSidebarGroup key={section.id}>
                <DimeSidebarGroupLabel>{section.title}</DimeSidebarGroupLabel>
                <DimeSidebarGroupContent>
                  <DimeSidebarMenu>
                    {visible.map((item) => {
                      const active = isItemActive(pathname, item);
                      const Icon = item.icon;
                      const icon = <Icon aria-hidden className="size-4 shrink-0" />;

                      return (
                        <DimeSidebarMenuItem key={'to' in item ? item.to : item.href}>
                          {'href' in item ? (
                            <DimeSidebarMenuButton asChild isActive={active} tooltip={item.label}>
                              <a href={item.href}>
                                {icon}
                                <span>{item.label}</span>
                              </a>
                            </DimeSidebarMenuButton>
                          ) : (
                            <DimeSidebarMenuButton asChild isActive={active} tooltip={item.label}>
                              <Link to={item.to}>
                                {icon}
                                <span>{item.label}</span>
                              </Link>
                            </DimeSidebarMenuButton>
                          )}
                        </DimeSidebarMenuItem>
                      );
                    })}
                  </DimeSidebarMenu>
                </DimeSidebarGroupContent>
              </DimeSidebarGroup>
            );
          })
        )}
      </DimeSidebarContent>

      <DimeSidebarSeparator className="mx-0" />
      <DimeSidebarFooter>
        <DimeSidebarMenu>
          <DimeSidebarMenuItem>
            <DimeSidebarMenuButton tooltip="Log out" onClick={onLogout}>
              <LogOut />
              <span>Log out</span>
            </DimeSidebarMenuButton>
          </DimeSidebarMenuItem>
        </DimeSidebarMenu>
      </DimeSidebarFooter>
      <DimeSidebarRail />
    </DimeSidebar>
  );
}
